import { router } from "@inertiajs/react";
import AppShell from "../../components/Shell/AppShell";
import { common, pick } from "../../lib/i18n";
import { CurrentUser } from "../../types";

const copy = {
  pl: {
    title: "Pliki z błędami migracji", pending: (n: number) => `${n} plików oczekuje na interwencję`,
    noErrors: "Brak plików z błędami", allMigrated: "Wszystkie pliki zostały pomyślnie zmigrowane.",
    file: "Plik", service: "Serwis", size: "Rozmiar", error: "Błąd", actions: "Akcje", skip: "Pomiń",
    previous: "← Poprzednia", next: "Następna →", pageLabel: (p: number) => `Strona ${p}`,
  },
  en: {
    title: "Files with migration errors", pending: (n: number) => `${n} files awaiting intervention`,
    noErrors: "No files with errors", allMigrated: "All files were migrated successfully.",
    file: "File", service: "Service", size: "Size", error: "Error", actions: "Actions", skip: "Skip",
    previous: "← Previous", next: "Next →", pageLabel: (p: number) => `Page ${p}`,
  },
};

type BlobRow = {
  id: number;
  key: string;
  filename: string;
  service_name: string;
  byte_size: number;
  migration_error?: string;
  owner_type?: string;
  owner_id?: number;
  updated_at: string;
};

type Props = {
  blobs: BlobRow[];
  total_count: number;
  page: number;
  current_user: CurrentUser;
  products?: { id: number; name: string }[];
};

function formatBytes(b: number) {
  if (b >= 1_048_576) return `${(b / 1_048_576).toFixed(1)} MB`;
  return `${(b / 1024).toFixed(0)} KB`;
}

export default function AdminBrandMigrations({ blobs, total_count, page, current_user, products = [] }: Props) {
  const t = pick(copy);
  const c = common();
  const initials = current_user.name.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();

  const act = (id: number, action_type: "retry" | "skip") => {
    router.patch(`/admin/brand_migrations/${id}`, { action_type });
  };

  return (
    <AppShell products={products} user={{ name: current_user.name, initials, role: current_user.role }}>
      <div className="page" style={{ maxWidth: 900 }}>
        <div className="page-hd">
          <div>
            <h2>{t.title}</h2>
            <p>{t.pending(total_count)}</p>
          </div>
        </div>

        {blobs.length === 0 ? (
          <div className="empty">
            <h4>{t.noErrors}</h4>
            <p>{t.allMigrated}</p>
          </div>
        ) : (
          <div className="panel">
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
              <thead>
                <tr style={{ borderBottom: "1px solid var(--line)" }}>
                  {[t.file, t.service, t.size, t.error, t.actions].map((h) => (
                    <th key={h} style={{ textAlign: "left", padding: "8px 10px", color: "var(--muted)", fontWeight: 500 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {blobs.map((b) => (
                  <tr key={b.id} style={{ borderBottom: "1px solid var(--line)" }}>
                    <td style={{ padding: "8px 10px" }}>
                      <div style={{ fontWeight: 500 }}>{b.filename}</div>
                      {b.owner_type && <div className="mono" style={{ fontSize: 10, color: "var(--muted-2)" }}>{b.owner_type} #{b.owner_id}</div>}
                    </td>
                    <td style={{ padding: "8px 10px" }}><span className="mono">{b.service_name}</span></td>
                    <td style={{ padding: "8px 10px" }}>{formatBytes(b.byte_size)}</td>
                    <td style={{ padding: "8px 10px", maxWidth: 240 }}>
                      {b.migration_error && (
                        <span style={{ color: "var(--danger)", fontSize: 11, wordBreak: "break-word" }}>{b.migration_error}</span>
                      )}
                    </td>
                    <td style={{ padding: "8px 10px" }}>
                      <div style={{ display: "flex", gap: 6 }}>
                        <button className="btn btn-secondary btn-sm" style={{ fontSize: 11 }} onClick={() => act(b.id, "retry")}>{c.actions.retry}</button>
                        <button className="btn btn-ghost btn-sm" style={{ fontSize: 11 }} onClick={() => act(b.id, "skip")}>{t.skip}</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {total_count > 30 && (
              <div style={{ padding: "10px", display: "flex", gap: 8, justifyContent: "flex-end" }}>
                {page > 1 && <a href={`/admin/brand_migrations?page=${page - 1}`} className="btn btn-ghost btn-sm">{t.previous}</a>}
                <span style={{ fontSize: 12, alignSelf: "center", color: "var(--muted)" }}>{t.pageLabel(page)}</span>
                {blobs.length === 30 && <a href={`/admin/brand_migrations?page=${page + 1}`} className="btn btn-ghost btn-sm">{t.next}</a>}
              </div>
            )}
          </div>
        )}
      </div>
    </AppShell>
  );
}
